import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { patchOf } from "../common/patch.js";
import { timestampSchema, unitPriceSchema, uuidSchema } from "../common/primitives.js";
export const priceSourceSchema = z.enum(["DISTRIBUTOR", "MANUAL", "INITIAL", "IMPORT"]);
export const PRICE_CHANGE_STATUSES = [
    "DRAFT",
    "PENDING_APPROVAL",
    "SCHEDULED",
    "APPLIED",
    "CANCELLED",
];
export const priceChangeStatusSchema = z.enum(PRICE_CHANGE_STATUSES);
const positivePriceSchema = unitPriceSchema.refine((value) => Number(value) > 0, "Fiyat sıfırdan büyük olmalıdır");
export const priceListSchema = z.object({
    id: uuidSchema,
    code: z.string(),
    name: z.string(),
    isDefault: z.boolean(),
    isActive: z.boolean(),
});
export const priceListListSchema = z.object({ items: z.array(priceListSchema) });
// ── Güncel fiyatlar ve geçmiş ────────────────────────────────────────────────
export const currentPriceSchema = z.object({
    stationId: uuidSchema,
    stationCode: z.string(),
    stationName: z.string(),
    productId: uuidSchema,
    productCode: z.string(),
    productName: z.string(),
    productColor: z.string().nullable(),
    unit: z.string(),
    unitPrice: unitPriceSchema.nullable().describe("KDV dâhil; hiç fiyat girilmemişse null"),
    validFrom: timestampSchema.nullable(),
    /** Planlanmış (henüz yürürlüğe girmemiş) bir sonraki fiyat. */
    upcoming: z
        .object({
        unitPrice: unitPriceSchema,
        effectiveAt: timestampSchema,
        requestId: uuidSchema,
        requestNo: z.string(),
    })
        .nullable(),
});
export const currentPriceListSchema = z.object({ asOf: timestampSchema, items: z.array(currentPriceSchema) });
export const currentPricesQuerySchema = z.object({
    stationId: uuidSchema.optional(),
    productId: uuidSchema.optional(),
    asOf: timestampSchema.optional().describe("Geçmişte ya da gelecekte bir an; verilmezse şimdi"),
});
export const priceHistoryEntrySchema = z.object({
    id: uuidSchema,
    stationId: uuidSchema,
    productId: uuidSchema,
    unitPrice: unitPriceSchema,
    validFrom: timestampSchema,
    source: priceSourceSchema,
    requestId: uuidSchema.nullable(),
    requestNo: z.string().nullable(),
    createdAt: timestampSchema,
});
export const priceHistoryPageSchema = pageOf(priceHistoryEntrySchema);
export const priceHistoryQuerySchema = pageQuerySchema.extend({
    stationId: uuidSchema,
    productId: uuidSchema,
});
// ── Fiyat değişikliği talepleri ──────────────────────────────────────────────
export const priceChangeItemSchema = z.object({
    stationId: uuidSchema,
    stationCode: z.string(),
    stationName: z.string(),
    productId: uuidSchema,
    productCode: z.string(),
    productName: z.string(),
    newUnitPrice: unitPriceSchema,
    /** Taslakta: şu an yürürlükteki fiyat. Uygulandıktan sonra: uygulama anındaki önceki fiyat (anlık görüntü). */
    previousUnitPrice: unitPriceSchema.nullable(),
});
export const priceChangeRequestSchema = z.object({
    id: uuidSchema,
    requestNo: z.string(),
    source: z.enum(["DISTRIBUTOR", "MANUAL"]),
    status: priceChangeStatusSchema,
    effectiveAt: timestampSchema,
    note: z.string().nullable(),
    createdByName: z.string().nullable(),
    approvedByName: z.string().nullable(),
    submittedAt: timestampSchema.nullable(),
    approvedAt: timestampSchema.nullable(),
    appliedAt: timestampSchema.nullable(),
    cancelledAt: timestampSchema.nullable(),
    cancelReason: z.string().nullable(),
    stationCount: z.number().int(),
    itemCount: z.number().int(),
    items: z.array(priceChangeItemSchema).describe("Liste ucunda boş döner; ayrıntı ucunda dolu"),
    version: z.number().int(),
    createdAt: timestampSchema,
});
export const priceChangeRequestPageSchema = pageOf(priceChangeRequestSchema);
export const listPriceChangeRequestsQuerySchema = pageQuerySchema.extend({
    status: priceChangeStatusSchema.optional(),
});
/**
 * Kalem ya bir İSTASYONA ya da bir FİYAT GRUBUNA verilir (Prompt 16, ADR 0016 §14): grup kalemi kayıtta grubun
 * istasyonlarına açılır; aynı istasyon × ürün için ayrıca istasyon kalemi verilmişse o kazanır (istisna).
 */
const requestItemSchema = z
    .object({
    stationId: uuidSchema.optional().describe("İstasyon (ya da `priceGroupId`)"),
    priceGroupId: uuidSchema.optional().describe("Fiyat grubu: kalem grubun tüm istasyonlarına açılır"),
    productId: uuidSchema,
    newUnitPrice: positivePriceSchema,
})
    .refine((item) => (item.stationId === undefined) !== (item.priceGroupId === undefined), "Kalem için istasyon YA DA fiyat grubu seçin (ikisi birden değil)");
const requestItemsSchema = z
    .array(requestItemSchema)
    .min(1, "En az bir fiyat girin")
    .max(5000)
    .refine((items) => new Set(items.map((item) => `${item.stationId ?? `g:${item.priceGroupId}`}:${item.productId}`)).size ===
    items.length, "Aynı istasyon (ya da grup) ve ürün için tek fiyat girilebilir");
export const createPriceChangeRequestSchema = z.object({
    source: z.enum(["DISTRIBUTOR", "MANUAL"]).default("MANUAL"),
    effectiveAt: timestampSchema.describe("Planlanan yürürlük zamanı"),
    note: z.string().trim().max(500).nullable().default(null),
    items: requestItemsSchema,
});
export const updatePriceChangeRequestSchema = patchOf(createPriceChangeRequestSchema).extend({
    version: z.number().int().positive(),
});
export const cancelPriceChangeRequestSchema = z.object({
    reason: z.string().trim().min(3, "İptal nedeni yazın").max(300),
});
// ── Toplu değişiklik önizlemesi (istasyon × ürün matrisi) ────────────────────
export const PRICE_ADJUSTMENT_MODES = ["PERCENT", "AMOUNT", "SET"];
export const priceAdjustmentSchema = z.object({
    mode: z
        .enum(PRICE_ADJUSTMENT_MODES)
        .describe("PERCENT: yüzde artır/azalt · AMOUNT: tutar ekle/çıkar · SET: sabit fiyat"),
    value: z.string().regex(/^-?\d{1,9}(\.\d{1,4})?$/, "Değer en fazla 4 ondalıklı sayı olmalıdır"),
    /** Sonucun yuvarlanacağı ondalık basamak (pompa fiyatları genellikle 2). */
    roundTo: z.number().int().min(0).max(4).default(2),
});
export const previewPriceChangeSchema = z
    .object({
    stationIds: z.array(uuidSchema).max(500).default([]),
    /** Prompt 16: seçilen fiyat gruplarının istasyonları da önizlemeye girer (istasyon listesiyle birleşir). */
    priceGroupIds: z
        .array(uuidSchema)
        .max(100)
        .default([])
        .describe("Fiyat grupları (istasyonlarına açılır)"),
    productIds: z.array(uuidSchema).min(1).max(50),
    adjustment: priceAdjustmentSchema,
})
    .refine((value) => value.stationIds.length > 0 || value.priceGroupIds.length > 0, "En az bir istasyon ya da fiyat grubu seçin");
export const priceChangePreviewSchema = z.object({
    items: z.array(z.object({
        stationId: uuidSchema,
        productId: uuidSchema,
        currentUnitPrice: unitPriceSchema.nullable(),
        newUnitPrice: unitPriceSchema
            .nullable()
            .describe("Hesaplanamadıysa (güncel fiyat yok ya da sonuç ≤ 0) null"),
        skippedReason: z.enum(["NO_CURRENT_PRICE", "NON_POSITIVE_RESULT"]).nullable(),
    })),
});
//# sourceMappingURL=pricing.js.map